import React, { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Grid, Float, Stars } from "@react-three/drei";
import { Physics } from "@react-three/rapier";
import * as THREE from "three";
import Robot, { WALK_START_X, WALK_END_X } from "./Robot";
import PillarMonoliths, { PILLAR_START_X, PILLAR_SPACING } from "./PillarMonoliths";
import PhysicsBalls from "./PhysicsBalls";

const CameraRig = ({ progressRef, isMobile }) => {
    useFrame(({ camera }, delta) => {
        const p = THREE.MathUtils.clamp(progressRef.current, 0, 1);
        const robotX = THREE.MathUtils.lerp(WALK_START_X, WALK_END_X, p);
        // pan the view left so the robot stages right-of-center, clear of the copy
        const viewOffset = isMobile ? 0 : 2.6;
        const tx = robotX * 0.92 - viewOffset;
        camera.position.x = THREE.MathUtils.damp(camera.position.x, tx, 3, delta);
        camera.position.y = THREE.MathUtils.damp(camera.position.y, isMobile ? 4.6 : 3.4, 3, delta);
        camera.position.z = THREE.MathUtils.damp(camera.position.z, isMobile ? 14.5 : 11.5, 3, delta);
        // mobile: aim above the robot so it stages low, clear of the stacked copy
        camera.lookAt(tx, isMobile ? 3.1 : 1.7, 0);
    });
    return null;
};

const ActiveLight = ({ activeRef }) => {
    const light = useRef();
    useFrame((_, delta) => {
        if (!light.current) return;
        const tx = PILLAR_START_X + activeRef.current * PILLAR_SPACING;
        light.current.position.x = THREE.MathUtils.damp(light.current.position.x, tx, 4, delta);
    });
    return <pointLight ref={light} position={[PILLAR_START_X, 4.5, 2.5]} color="#ec4899" intensity={16} distance={10} />;
};

const RobotLight = ({ progressRef }) => {
    const light = useRef();
    useFrame((_, delta) => {
        if (!light.current) return;
        const p = THREE.MathUtils.clamp(progressRef.current, 0, 1);
        const tx = THREE.MathUtils.lerp(WALK_START_X, WALK_END_X, p);
        light.current.position.x = THREE.MathUtils.damp(light.current.position.x, tx, 4, delta);
    });
    return <pointLight ref={light} position={[WALK_START_X, 3.2, 4.6]} color="#93c5fd" intensity={17} distance={11} />;
};

const AmbientOrbs = ({ count = 7 }) => {
    const orbs = useMemo(
        () =>
            Array.from({ length: count }, (_, i) => ({
                pos: [-16 + i * 5.2, 3.5 + (i % 3) * 1.4, -5 - (i % 2) * 2],
                color: i % 3 === 0 ? "#a855f7" : i % 3 === 1 ? "#ec4899" : "#7dd3fc",
                scale: 0.16 + (i % 3) * 0.07,
            })),
        [count]
    );
    return (
        <group>
            {orbs.map((o, i) => (
                <Float key={i} speed={2} rotationIntensity={0.4} floatIntensity={2.2}>
                    <mesh position={o.pos}>
                        <sphereGeometry args={[o.scale, 16, 16]} />
                        <meshStandardMaterial color={o.color} emissive={o.color} emissiveIntensity={2.2} />
                    </mesh>
                </Float>
            ))}
        </group>
    );
};

const HeroScene = ({ progressRef, activeRef }) => {
    const ballsRef = useRef();
    const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

    return (
        <Canvas
            dpr={isMobile ? [1, 1.2] : [1, 1.75]}
            camera={{ position: [WALK_START_X * 0.92 - (isMobile ? 0 : 2.6), isMobile ? 4.6 : 3.4, isMobile ? 14.5 : 11.5], fov: isMobile ? 50 : 42 }}
            gl={{ antialias: true, alpha: true }}
            data-testid="hero-3d-canvas"
        >
            <fog attach="fog" args={["#07040d", 16, 42]} />
            <ambientLight intensity={0.35} />
            <directionalLight position={[6, 10, 6]} intensity={1.1} color="#c4b5fd" />
            <pointLight position={[-6, 5, -4]} intensity={26} color="#a855f7" distance={20} />
            <pointLight position={[8, 2.5, 5]} intensity={18} color="#7dd3fc" distance={16} />
            <ActiveLight activeRef={activeRef} />
            <RobotLight progressRef={progressRef} />
            <Stars radius={60} depth={30} count={isMobile ? 450 : 1400} factor={3} saturation={0.6} fade speed={0.6} />

            <Grid
                position={[0, 0.02, 0]}
                args={[80, 80]}
                cellSize={0.85}
                cellThickness={0.6}
                cellColor="#241538"
                sectionSize={4.25}
                sectionThickness={1.1}
                sectionColor="#a855f7"
                fadeDistance={46}
                fadeStrength={2.4}
                infiniteGrid
            />

            <Physics gravity={[0, -14, 0]}>
                <Robot progressRef={progressRef} />
                <PillarMonoliths activeRef={activeRef} />
                <PhysicsBalls ref={ballsRef} />
            </Physics>

            <AmbientOrbs count={isMobile ? 3 : 7} />

            {/* invisible interaction plane — click drops a physics sphere */}
            <mesh
                position={[0, 4, 4.5]}
                onPointerDown={(e) => {
                    e.stopPropagation();
                    ballsRef.current?.spawn(e.point);
                }}
            >
                <planeGeometry args={[90, 26]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>

            <CameraRig progressRef={progressRef} isMobile={isMobile} />
        </Canvas>
    );
};

export default HeroScene;
